import Vue from 'vue';
import Meta from 'vue-meta';
import Router from 'vue-router';
import { sync } from 'vuex-router-sync';
import routes from './routes';
import store from '../store';
import { loadLanguageAsync } from '@/plugins/i18n';
import { BarcodeScanner } from '@capacitor-community/barcode-scanner';

Vue.use(Meta);
Vue.use(Router);

/**
 * Create a new router instance.
 *
 * @param  {Array} routes
 * @return {Router}
 */
function make (routes) {
  const router = new Router({
    base: process.env.NODE_ENV === 'production' ? '/app/' : '/',
    routes,
    scrollBehavior,
    mode: 'history'
  });

  // Register before guard.
  router.beforeEach(async (to, from, next) => {
    if (document.getElementById('app') && document.getElementById('app').classList.contains('barcodescanner-active')) {
      document.getElementById('app').classList.remove('barcodescanner-active');
      next(false);
      await BarcodeScanner.stopScan();
      await BarcodeScanner.showBackground();
      return;
    }

    if (!store.getters['auth/check'] && store.getters['auth/token']) {
      try {
        await store.dispatch('auth/fetchUser');
      } catch (e) {
        // do nothing
      }
    }

    const lang = to.params.lang;
    await loadLanguageAsync(lang);
    await setLayout(router, to);
    next();
  });

  return router;
}

const router = make(routes({ authGuard, guestGuard }));

sync(store, router);

export default router;

/**
 * Set the application layout from the matched page component.
 *
 * @param {Router} router
 * @param {Route} to
 */
async function setLayout (router, to) {
  // Get the first matched component.
  let [component] = router.getMatchedComponents({ ...to });

  if (component instanceof Function) {
    const wrappedComponent = await component();
    component = wrappedComponent.default;
  }

  if (component) {
    router.app.$nextTick(() => {
      // Set application layout.
      router.app.setLayout(component.layout || '');
    });
  }
}

/**
 * Redirect to login if guest.
 *
 * @param  {Array} routes
 * @return {Array}
 */
function authGuard (routes) {
  return beforeEnter(routes, (to, from, next) => {
    if (!store.getters['auth/check']) {
      next({ name: 'login', params: { last: to.name } });
    } else {
      next();
    }
  });
}

/**
 * Redirect home if authenticated.
 *
 * @param  {Array} routes
 * @return {Array}
 */
function guestGuard (routes) {
  return beforeEnter(routes, (to, from, next) => {
    if (store.getters['auth/check']) {
      next({ name: 'home' });
    } else {
      next();
    }
  });
}

/**
 * Apply beforeEnter guard to the routes.
 *
 * @param  {Array} routes
 * @param  {Function} beforeEnter
 * @return {Array}
 */
function beforeEnter (routes, beforeEnter) {
  return routes.map(route => ({ ...route, beforeEnter }));
}

/**
 * @param  {Route} to
 * @param  {Route} from
 * @param  {Object|undefined} savedPosition
 * @return {Object}
 */
function scrollBehavior (to, from, savedPosition) {
  if (savedPosition) {
    return savedPosition;
  }

  const position = {};

  if (to.hash) {
    position.selector = to.hash;
  }

  if (to.matched.some(m => m.meta.scrollToTop)) {
    position.x = 0;
    position.y = 0;
  }

  return position;
}
